import React from 'react';
import { Button, Popover } from 'antd';
import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { FilterFieldBlock, activeValueCount, clearFields, fieldKeys, isFieldActive, optionText } from './controls';
import useFilterOptions from './useFilterOptions';
import { FilterField, SelectFilterField } from './types';

/**
 * A value the page arrived with is the list's own default, not something the
 * reader chose — so the column keeps its name until they narrow it themselves.
 */
const isChosen = (field: FilterField, values: Record<string, any>, defaults?: Record<string, any>) =>
    isFieldActive(field, values)
    && fieldKeys(field).some((key) => String(values[key] ?? '') !== String(defaults?.[key] ?? ''));

/**
 * What a select is filtering on, in words. The value is an id for anything
 * backed by a resource, so the options have to be resolved before it can be
 * shown — until they are (or if the value is not among them) the column keeps
 * its own name, which is never wrong, only less specific.
 *
 * Mounted only while the field is active, so a column nobody has filtered
 * fetches nothing.
 */
const SelectValue = ({
    field,
    values,
    fallback,
}: {
    field: SelectFilterField;
    values: Record<string, any>;
    fallback: string;
}) => {
    const { options } = useFilterOptions(field, values);
    const raw = values[field.name];
    const first = Array.isArray(raw) ? raw[0] : String(raw).split(',')[0];
    const match = options.find((option) => String(option.value) === String(first));
    return <>{optionText(match) ?? fallback}</>;
};

const ColumnValue = ({
    field,
    values,
    fallback,
}: {
    field: FilterField;
    values: Record<string, any>;
    fallback: string;
}) => {
    if (field.type === 'select') return <SelectValue field={field} values={values} fallback={fallback} />;
    // a search holds its own words already; anything else has no reading worth
    // putting in a header
    if (field.type === 'search') return <>{String(values[field.name])}</>;
    return <>{fallback}</>;
};

/**
 * A table column header that doubles as that column's filter: the whole header
 * is a button that opens the field(s) behind it, and it carries a funnel icon at
 * all times so the column reads as filterable before anyone hovers it.
 *
 * Once a filter is set the header stops naming the column and names what it is
 * filtering on instead — "NewApp1" rather than "Application" — with a +n when
 * more than one value is picked. The column's own name stays in the tooltip and
 * the accessible label.
 *
 * Columns with nothing to filter render as a plain, quieter label, which also
 * keeps the interactive ones standing out.
 */
const ColumnFilter = ({
    label,
    icon,
    fields,
    schema,
    values,
    defaults,
    onChange,
    align = 'left',
    dataCy,
}: {
    label: string;
    /** stands for the column, once, instead of on every row beneath it */
    icon?: React.ReactNode;
    /** the filters this column offers — empty for a plain, unfilterable header */
    fields: FilterField[];
    /** the page's whole filter schema, for cascade clearing */
    schema: FilterField[];
    values: Record<string, any>;
    /** what the list filters by before anyone touches it */
    defaults?: Record<string, any>;
    onChange: (next: Record<string, any>) => void;
    align?: 'left' | 'right';
    dataCy?: string;
}) => {
    // a field hidden for this POV is not part of its filter set
    const usable = fields.filter((field) => !field.hidden);
    const glyph = icon ? <span className="ft-col__ico" aria-hidden="true">{icon}</span> : null;

    if (usable.length === 0) {
        return (
            <span className={`ft-col ft-col--${align}`}>
                {glyph}
                {label}
            </span>
        );
    }

    // One column can front several filters (runtime + change state), so both the
    // count and the name shown come from the whole set. Active is what tints the
    // header; only a filter the reader chose replaces its name.
    const active = usable.some((field) => isFieldActive(field, values));
    const chosen = usable.find((field) => isChosen(field, values, defaults));
    const count = chosen
        ? usable.reduce((total, field) => total + activeValueCount(field, values), 0)
        : 0;

    return (
        <Popover
            trigger="click"
            placement={align === 'right' ? 'bottomRight' : 'bottomLeft'}
            classNames={{ root: 'ft-popover' }}
            content={(
                <div className="ft-popover__body ft-popover__body--column">
                    {usable.map((field) => (
                        <FilterFieldBlock
                            key={field.name}
                            field={field}
                            schema={schema}
                            values={values}
                            onChange={onChange}
                        />
                    ))}
                    {active && (
                        <Button
                            className="ft-clear"
                            type="link"
                            size="small"
                            onClick={() => clearFields(usable, schema, values, onChange)}
                        >
                            Clear this filter
                        </Button>
                    )}
                </div>
            )}
        >
            <button
                type="button"
                data-cy={dataCy}
                title={chosen ? label : undefined}
                aria-label={`Filter by ${label}`}
                className={`ft-col ft-col--${align} ft-col--button${active ? ' ft-col--active' : ''}`}
            >
                {glyph}
                <span className={`ft-col__label${chosen ? ' ft-col__label--value' : ''}`}>
                    {chosen
                        ? <ColumnValue field={chosen} values={values} fallback={label} />
                        : label}
                </span>
                {count > 1 && <span className="ft-col__count">{`+${count - 1}`}</span>}
                {active
                    ? <FilterFilled className="ft-col__icon" aria-hidden="true" />
                    : <FilterOutlined className="ft-col__icon" aria-hidden="true" />}
            </button>
        </Popover>
    );
};

export default ColumnFilter;
